/**
 * FibiOTCard.jsx · v101.196.0 · Evidencia en el detalle de la OT: conversaciones de WhatsApp (Fibi y agendadores)
 * con el cliente, descarga en PDF y botón para que Fibi contacte al cliente.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Bot, Download, Loader2, MessageSquare } from "lucide-react";

const ROLES_AGENTE = ["super_admin", "admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"];
const hora = (v) => (v ? new Date(v).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const ESTADO = { en_espera: "En espera de agente", humano: "Con agente", bot: "Con Fibi", cerrada: "Cerrada" };

export default function FibiOTCard({ odooTaskId }) {
  const { user } = useAuth() || {};
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const agente = ROLES_AGENTE.includes(String(user?.role || "").toLowerCase());
  const load = useCallback(() => {
    if (!odooTaskId) return;
    api.get(`/v1/fibi/ot/${odooTaskId}`).then(({ data }) => setItems(data.items || [])).catch((e) => setItems(e?.response?.status === 403 ? null : []));
  }, [odooTaskId]);
  useEffect(() => { load(); }, [load]);
  const pdf = async () => {
    try {
      const r = await api.get(`/v1/fibi/ot/${odooTaskId}/evidencia.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `WhatsApp_OT_${odooTaskId}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error("No se pudo descargar el PDF"); }
  };
  const contactar = async () => {
    setBusy(true);
    try {
      await api.post("/v1/fibi/iniciar", { odoo_task_id: odooTaskId }, { noProcess: true });
      toast.success("Fibi saludó al cliente por WhatsApp"); load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo contactar");
    } finally { setBusy(false); }
  };
  if (items === null) return null;
  return (
    <div style={{ borderRadius: 20, padding: 16, background: "#ECF0F5", boxShadow: "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)" }} data-testid="card-fibi-whatsapp">
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0A3747", fontSize: 14 }}>
          <MessageSquare size={16} /> Conversaciones WhatsApp ({items.length})
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {items.length > 0 && <button onClick={pdf} style={{ border: 0, borderRadius: 10, padding: "6px 10px", background: "#E4EAF0", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "inline-flex", gap: 5, alignItems: "center" }}><Download size={13} /> PDF evidencia</button>}
          {agente && <button onClick={contactar} disabled={busy} style={{ border: 0, borderRadius: 10, padding: "6px 10px", background: "#0A3747", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "inline-flex", gap: 5, alignItems: "center" }}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Bot size={13} />} Contactar con Fibi</button>}
        </div>
      </div>
      {items.length === 0 && <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Sin conversaciones de WhatsApp con el cliente para esta OT.</div>}
      {items.map((c) => (
        <div key={c.id} style={{ marginTop: 10, borderRadius: 14, background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "8px 10px" }}>
          <button onClick={() => setAbierta(abierta === c.id ? null : c.id)} style={{ width: "100%", border: 0, background: "transparent", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5 }}>
            <span><b>#{c.id}</b> · {hora(c.created_at)} · {ESTADO[c.estado] || c.estado}{c.asignado_nombre ? ` · ${c.asignado_nombre}` : ""} · {c.mensajes.filter((m) => m.direccion !== "nota").length} mensajes</span>
            <span style={{ color: "#0F7FA8", fontWeight: 600 }}>{abierta === c.id ? "Ocultar" : "Ver"}</span>
          </button>
          {c.resumen && <div style={{ fontSize: 11.5, color: "#78350F", marginTop: 4 }}>Resumen: {c.resumen}</div>}
          {abierta === c.id && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
              {c.mensajes.map((m) => (
                <div key={m.id} style={{ fontSize: 12, color: m.direccion === "nota" ? "#64748B" : "#0F172A", fontStyle: m.direccion === "nota" ? "italic" : "normal" }}>
                  <span style={{ color: "#64748B" }}>{hora(m.created_at)} · </span>
                  <b>{m.autor === "cliente" ? "Cliente" : m.autor === "fibi" ? "Fibi" : m.autor === "agente" ? (m.autor_nombre || "Agente") : "Sistema"}:</b>{" "}
                  <span style={{ whiteSpace: "pre-wrap" }}>{/^\[sticker:/.test(m.texto || "") ? "🖼️ sticker" : m.texto}</span>
                </div>
              ))}
              {agente && <Link to={`/whatsapp/conversaciones?c=${c.id}`} style={{ fontSize: 12, color: "#0F7FA8", fontWeight: 700 }}>Abrir en Conversaciones →</Link>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
